import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BellRing, Check, CircleSlash, Clock, Paperclip, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, formatError } from "@/lib/api";

export const STAGE_LABELS = { new_join: "New join", lafha: "LAFHA", payroll: "Payroll", extension: "Extension" };
const PILL = {
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  requested: "bg-sky-50 text-sky-700 border-sky-200",
  received: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_required: "bg-slate-50 text-slate-400 border-slate-200",
};
const PILL_LABEL = { not_started: "Not started", requested: "Requested", received: "Received", not_required: "Not required" };

function ItemRow({ it, canWrite, update, upload, chase }) {
  const file = useRef(null);
  const done = it.status === "received" || it.status === "not_required";
  return (
    <li className={`flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3 ${it.overdue ? "bg-amber-50/70" : ""}`} data-testid={`checklist-item-${it.code}-${it.cycle}`}>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${it.status === "not_required" ? "line-through text-slate-400" : "text-slate-800"}`}>
          {it.label}{!it.required && <span className="ml-2 text-[11px] text-slate-400">(optional)</span>}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
          {it.completed_by && <span>{PILL_LABEL[it.status]} by {it.completed_by} · {fmtDateTime(it.completed_at)}</span>}
          {it.status === "requested" && <span className={it.overdue ? "text-amber-700 font-medium" : ""}>Requested {fmtDateTime(it.requested_at)}{it.overdue && " · overdue"}</span>}
          {it.document_name && <span className="text-blue-600"><Paperclip className="inline h-3 w-3" /> {it.document_name}</span>}
        </p>
      </div>
      <div className="flex items-center gap-1.5">
        {it.overdue && <AlertTriangle className="h-4 w-4 text-amber-600" data-testid={`checklist-overdue-${it.code}`} />}
        {it.overdue && canWrite && <Button size="sm" variant="outline" className="h-7 px-2 text-amber-800 border-amber-300 bg-amber-50 hover:bg-amber-100" onClick={() => chase(it)} data-testid={`checklist-chase-${it.code}-${it.cycle}`}><BellRing className="h-3.5 w-3.5 mr-1" />Chase</Button>}
        <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 ${PILL[it.status]}`} data-testid={`checklist-status-${it.code}-${it.cycle}`}>{PILL_LABEL[it.status]}</span>
        {canWrite && (
          <>
            {!done && <Button size="sm" variant="outline" className="h-7 px-2 text-emerald-700" onClick={() => update(it, "received")} data-testid={`checklist-received-${it.code}-${it.cycle}`}><Check className="h-3.5 w-3.5 mr-1" />Received</Button>}
            {it.status === "not_started" && <Button size="sm" variant="ghost" className="h-7 px-2" title="Mark requested" onClick={() => update(it, "requested")} data-testid={`checklist-requested-${it.code}-${it.cycle}`}><Clock className="h-3.5 w-3.5" /></Button>}
            {!done && <Button size="sm" variant="ghost" className="h-7 px-2" title="Not required" onClick={() => update(it, "not_required")} data-testid={`checklist-notrequired-${it.code}-${it.cycle}`}><CircleSlash className="h-3.5 w-3.5" /></Button>}
            {!done && (
              <>
                <input ref={file} type="file" className="hidden" onChange={(e) => e.target.files[0] && upload(it, e.target.files[0])} data-testid={`checklist-file-${it.code}-${it.cycle}`} />
                <Button size="sm" variant="ghost" className="h-7 px-2" title="Upload document" onClick={() => file.current.click()} data-testid={`checklist-upload-${it.code}-${it.cycle}`}><Paperclip className="h-3.5 w-3.5" /></Button>
              </>
            )}
            {it.status !== "not_started" && <Button size="sm" variant="ghost" className="h-7 px-2" title="Reset" onClick={() => update(it, "not_started")} data-testid={`checklist-reset-${it.code}-${it.cycle}`}><RotateCcw className="h-3.5 w-3.5 text-slate-400" /></Button>}
          </>
        )}
      </div>
    </li>
  );
}

export function OnboardingTab({ c, onStatusChange, reloadKey, onChase }) {
  const { canWrite } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/contracts/${c.id}/checklist`).then((r) => setData(r.data)), [c.id]);
  useEffect(() => { load(); }, [load, reloadKey, c.status, c.lafha_eligible, c.employing_entity]);

  const update = async (it, status) => {
    try { setData((await api.patch(`/contracts/${c.id}/checklist/${it.id}`, { status })).data); } catch (e) { toast.error(formatError(e)); }
  };
  const upload = async (it, f) => {
    const fd = new FormData();
    fd.append("file", f);
    try { setData((await api.post(`/contracts/${c.id}/checklist/${it.id}/upload`, fd)).data); toast.success(`${f.name} uploaded and marked received`); } catch (e) { toast.error(formatError(e)); }
  };

  const chase = async (it) => {
    try { onChase((await api.get(`/contracts/${c.id}/checklist/${it.id}/chase`)).data); } catch (e) { toast.error(formatError(e)); }
  };
  if (!data) return null;
  return (
    <div className="space-y-5" data-testid="onboarding-tab">
      {data.ready_for_payroll_prompt && canWrite && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-violet-50 border border-violet-200 rounded-lg px-4 py-3 animate-fade-up" data-testid="ready-for-payroll-prompt">
          <p className="text-sm text-violet-900 flex-1">Every required new-join item is received. <strong>Move to Ready for payroll?</strong></p>
          <Button size="sm" onClick={() => onStatusChange("ready_for_payroll")} data-testid="move-ready-for-payroll-button">Move to Ready for payroll</Button>
        </div>
      )}
      {data.groups.map((g) => (
        <section key={`${g.stage}-${g.cycle}`} className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid={`checklist-group-${g.stage}-${g.cycle}`}>
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-4">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider whitespace-nowrap">{STAGE_LABELS[g.stage]}{g.stage === "extension" && ` #${g.cycle}`}</h3>
            <Progress value={g.total ? (g.done / g.total) * 100 : 0} className="h-2 flex-1" />
            <span className="text-xs font-mono text-slate-500 whitespace-nowrap" data-testid={`checklist-progress-${g.stage}-${g.cycle}`}>{g.done} of {g.total}</span>
          </div>
          <ul className="divide-y divide-slate-100">
            {g.items.map((it) => <ItemRow key={it.id} it={it} canWrite={canWrite} update={update} upload={upload} chase={chase} />)}
          </ul>
        </section>
      ))}
      {!data.groups.length && <p className="text-sm text-slate-400">No checklist items apply to this contract.</p>}
    </div>
  );
}
